import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  ListItemIcon,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField
} from '@mui/material';

import Logout from '@mui/icons-material/Logout';
import SyncLockOutlinedIcon from '@mui/icons-material/SyncLockOutlined';
import { useAutoLogout } from 'hooks/useAutoLogout';
import Swal from 'sweetalert2';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { baseURLFinanceVendor } from 'config/apiConfig';

const Profile = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const { handleLogout } = useAutoLogout();
  const open = Boolean(anchorEl);

  const email = localStorage.getItem('email');
  const role = localStorage.getItem('ROLE');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangePassword = () => {
    setPasswordOpen(true);
    handleClose();
  };

  const handlePasswordClose = () => {
    setPasswordOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmitPassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      handlePasswordClose();
      Swal.fire({
        title: 'Missing Fields',
        text: 'Please fill in all fields.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#f39c12',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,

      });
      return;
    }

    if (newPassword !== confirmPassword) {
      handlePasswordClose();
      Swal.fire({
        title: 'Passwords Don’t Match',
        text: 'New Password and Confirm Password do not match.',
        icon: 'error',
        confirmButtonText: 'Retry',
        confirmButtonColor: '#d33',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
      });
      return;
    }

    try {

      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${baseURLFinanceVendor}ChangePassword`,
        {
          email: email,
          password: newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data) {
        handlePasswordClose();
        Swal.fire({
          title: 'Password Changed!',
          text: res.data.message,
          icon: 'success',
          confirmButtonText: 'Great 🎉',
          confirmButtonColor: '#7E00D1',
          timer: 3500,
          timerProgressBar: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
        });
      } else {
        handlePasswordClose();
        Swal.fire({
          title: 'Error',
          text: res.data?.message || 'Something went wrong!',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#d33',
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
        });
      }
    } catch (error) {
      handlePasswordClose();
      console.error(error);
      Swal.fire({
        title: 'Server Error',
        text: 'Please try again later.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
      });
    }
  };


  return (
    <>
      {/* Avatar Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '0.8rem' }}>
        <Tooltip
          title={
            <div className="flex flex-col justify-center items-center">
              <p className="text-[0.85rem]">{`${email}`}</p>
              <p className="text-[0.85rem]">{`(${role})`}</p>
            </div>
          }
        >
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32, backgroundColor: 'rgb(255, 128, 66)' }}>
              <p className="text-[1.1rem] font-semibold">{email?.charAt(0).toUpperCase()}</p>
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info */}
        <MenuItem>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              py: 0,
              textAlign: 'center'
            }}
          >
            <Avatar sx={{ backgroundColor: 'rgb(255, 128, 66)', width: 35, height: 35, mb: 0 }}>
              <p className="text-[1.1rem] font-semibold">{email?.charAt(0).toUpperCase()}</p>
            </Avatar>
            <Typography variant="body1" fontWeight="bold">
              {email}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight="bold"
              sx={{ fontSize: '14px' }}
            >
              ({role})
            </Typography>
          </Box>
        </MenuItem>

        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.2)' }} />

        {/* Change Password */}
        <MenuItem
          onClick={handleChangePassword}
        >
          <ListItemIcon>
            <SyncLockOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Change Password
        </MenuItem>

        {/* Logout */}
        <MenuItem
          onClick={() => {
            handleClose();
            handleLogout();
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordOpen}
        onClose={handlePasswordClose}
        PaperProps={{
          sx: { borderRadius: 2, width: 370, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", textAlign: "center", fontSize: "1.2rem" }}>
          🔐 Change Password
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Current Password */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type={showPassword.current ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => togglePassword("current")} edge="end">
                      {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            {/* New Password */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword.new ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => togglePassword("new")} edge="end">
                      {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            {/* Confirm Password */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword.confirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => togglePassword("confirm")} edge="end">
                      {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "end", px: 2, pb: 2 }}>
          <Button
            onClick={handlePasswordClose}
            variant="outlined"
            size="small"
            sx={{ borderRadius: "6px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPassword}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "#7E00D1",
              borderRadius: "6px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#5c00a3" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Profile;
